<template>
	<!-- eslint-disable-next-line vuejs-accessibility/no-static-element-interactions -->
	<div
		class="relative"
		data-motion-region
		:data-motion-autoplay="items.length > 1 ? 'true' : undefined"
		:data-motion-duration-ms="
			items.length > 1 ? AUTOPLAY_INTERVAL_MS : undefined
		"
		@focusin="setActive"
		@focusout="setInActive"
		@mouseover="setActive"
		@mouseout="setInActive"
	>
		<div class="grid grid-cols-1 grid-rows-1">
			<div
				v-for="(item, idx) in items"
				:key="idx"
				:class="idx === currentIndex ? 'opacity-100' : 'opacity-0 -z-10'"
				class="col-start-1 row-start-1 transition-opacity duration-1000 ease-in-out motion-reduce:transition-none"
			>
				<slot name="item" v-bind="item"></slot>
			</div>
		</div>

		<button
			v-if="items.length > 1 && preferredMotion !== 'reduce'"
			type="button"
			data-motion-pause-control
			class="absolute top-2 right-2 z-20 flex size-10 cursor-pointer items-center justify-center rounded-full bg-black/70 text-white hover:bg-black focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
			:aria-label="userPaused ? t('resume') : t('pause')"
			:aria-pressed="userPaused"
			@click.stop="userPaused = !userPaused"
		>
			<v-icon
				:key="userPaused ? 'play' : 'pause'"
				:icon="userPaused ? 'play' : 'pause'"
				class="text-xl"
			/>
		</button>
	</div>
</template>

<script setup lang="ts" generic="T extends object">
import {useIntervalFn, usePreferredReducedMotion} from "@vueuse/core";

const properties = defineProps<{
	items: T[];
}>();

const AUTOPLAY_INTERVAL_MS = 5000;

provide("sb.inWideSlider", true);

const currentIndex = ref(0);
const userPaused = ref(false);
const interactionPaused = ref(false);
const preferredMotion = usePreferredReducedMotion();
const {t} = useI18n();

const isPaused = computed(
	() =>
		userPaused.value ||
		interactionPaused.value ||
		preferredMotion.value === "reduce",
);

function move(index: number) {
	currentIndex.value = index;

	if (currentIndex.value >= properties.items.length) {
		currentIndex.value = 0;
	}
}

const {pause, resume} = useIntervalFn(
	() => {
		move(currentIndex.value + 1);
	},
	AUTOPLAY_INTERVAL_MS,
	{immediate: false},
);

onMounted(() => {
	watch(
		isPaused,
		(paused) => {
			if (paused || properties.items.length < 2) {
				pause();
			} else {
				resume();
			}
		},
		{immediate: true},
	);
});

function setActive() {
	interactionPaused.value = true;
}

function setInActive() {
	interactionPaused.value = false;
}
</script>

<i18n lang="json">
{
	"et": {
		"pause": "Peata slaidiseanss",
		"resume": "Jätka slaidiseanssi"
	},
	"fi": {
		"pause": "Pysäytä diaesitys",
		"resume": "Jatka diaesitystä"
	}
}
</i18n>
